import pick from 'lodash/pick'
import {
  compose,
  HandleCreators,
  withHandlers,
  withProps,
  withState,
} from 'recompose'
import { DistributorSFC } from './distributor.sfc'
import {
  DistributorOption,
  DistributorProps,
  DistributorSFCProps,
  Handlers,
  HandlersProps,
  LookupMap,
} from './types'

const CUSTOM = 'custom'

const makeName = (dist: DistributorOption): string =>
  `${dist.name} (${dist.category}${
    dist.subcategory ? `, ${dist.subcategory}` : ''
  })`

// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
export const _propsEnhancer = ({
  distributor,
  distributorList,
  isRequiredNotify,
}: DistributorProps) => {
  const isCustom =
    distributor.name !== undefined && distributor.mode !== 'reference'
  let isEmpty = false

  // distributorList = {...distributorList, { id: '123421424', name: ""}}
  /**
   * Create list of options with type { id: string; name: string; } for typeahead.
   * Any identical names will be converted to use the category and subcategory in the name
   * for distinction per EL-2530
   */
  const duplicates: string[] = [] // remove after full list is normalized
  const distributorLookup = distributorList.reduce<LookupMap>((lookup, d) => {
    let distName
    if (lookup[d.name]) {
      if (!duplicates.includes(d.name)) {
        // update existing distributor
        const existingDistributor = lookup[d.name]
        duplicates.push(d.name)

        const updatedName = makeName(existingDistributor)
        lookup[updatedName] = {
          ...existingDistributor,
          name: updatedName,
        }
      }

      distName = makeName(d)
    } else {
      distName = d.name
    }
    return {
      ...lookup,
      [distName]: {
        ...pick(d, 'id', 'category', 'subcategory', 'archived'),
        name: distName,
      },
    }
  }, {})

  // remove duplicate key
  duplicates.map((key) => {
    delete distributorLookup[key]
  })

  // Generate a list of all distributors, both active and archived
  const allDistributors = [
    { id: CUSTOM, name: 'Custom' },
    ...Object.values(distributorLookup),
  ]

  // Filter out archived distributors in the dropdown options
  const distributorOptions = allDistributors.filter((d) => !d.archived)

  let {
    reference_id: referenceId = '',
    name = '',
    category = '',
    subcategory = '',
  } = distributor

  const selectedDistributor = distributorList.find(
    ({ id }) => referenceId === id
  )

  if (isCustom) {
    referenceId = CUSTOM
  } else if (distributor.mode === 'reference') {
    if (selectedDistributor) {
      name = selectedDistributor.name
      category = selectedDistributor.category as string
      subcategory = selectedDistributor.subcategory || ''

      // Add the selected distributor to the list if it is archived
      if (selectedDistributor.archived) {
        distributorOptions.push({
          name,
          category,
          subcategory,
          id: referenceId,
          archived: selectedDistributor.archived,
        })
      }
    } else {
      // If can't find the reference from the distributorList, use referenceId as the name and extend the options
      // for display purpose
      distributorOptions.push({
        id: referenceId,
        name: referenceId,
      })
    }
  } else {
    isEmpty = true
  }

  const selectedOption =
    selectedDistributor ||
    distributorOptions.find((option) => option.id === referenceId)

  return {
    distributorOptions,
    isCustom,
    referenceId,
    typeaheadSelected: selectedOption ? [selectedOption] : [],
    name,
    category,
    subcategory,
    distributorRequired: isRequiredNotify && isEmpty,
    nameRequired: isRequiredNotify && isCustom && !distributor.name,
    categoryRequired: isRequiredNotify && isCustom && !distributor.category,
  }
}

export const _handlers: HandleCreators<HandlersProps, Handlers> = {
  setDistributor: ({ referenceId, applyDistributor, setInitFocused }) => (
    selected
  ) => {
    if (selected.length > 0) {
      if (selected[0].id !== referenceId) {
        const newReferenceId = selected[0].id
        if (newReferenceId === CUSTOM) {
          applyDistributor({
            mode: CUSTOM,
            name: '',
            category: '',
            subcategory: '',
          })
        } else {
          applyDistributor({
            mode: 'reference',
            // eslint-disable-next-line @typescript-eslint/camelcase
            reference_id: newReferenceId,
          })
        }
      }
      setInitFocused(true)
    }
  },
  updateCustomDistributor: (props) => (key, value) => {
    const data = {
      mode: CUSTOM,
    }
    data[key] = value
    props.applyDistributor({
      ...props.distributor,
      ...data,
    })
  },
  typeaheadOnBlur: (props) => (event) => {
    if (event.target.value) {
      props.setInitFocused(true)
    } else {
      // Clear distributor
      props.applyDistributor({})
    }
  },
  isTyping: (props) => () => {
    if (props.initFocused) {
      props.setInitFocused(false)
    }
  },
}
DistributorSFC.displayName = 'Distributor'

export default compose<DistributorSFCProps, DistributorProps>(
  withState('initFocused', 'setInitFocused', true),
  withProps(_propsEnhancer),
  withHandlers(_handlers)
)(DistributorSFC)
